import { useEffect, useState } from 'react';
import { colors, spacing } from '../theme';
import Card from '../components/Card';
import Button from '../components/Button';
import SearchBar from '../components/SearchBar';
import Table from '../components/Table';
import EmptyState from '../components/EmptyState';
import SkeletonLoader from '../components/SkeletonLoader';
import IncomingDetailPanel from './IncomingDetailPanel';
import { useIncomingRecords } from './hooks/useIncomingRecords';

const statusColors = {
  unmatched: { bg: '#fff5f5', color: colors.error },
  suggested: { bg: '#fffbeb', color: '#b45309' },
  matched: { bg: '#ecfdf5', color: colors.success },
  ignored: { bg: colors.gray100, color: colors.midGray },
};

function StatusPill({ status }) {
  const style = statusColors[status] || statusColors.unmatched;
  return (
    <span
      style={{
        display: 'inline-block',
        fontSize: '0.75rem',
        fontWeight: 600,
        padding: '0.15rem 0.5rem',
        borderRadius: '999px',
        backgroundColor: style.bg,
        color: style.color,
        textTransform: 'capitalize',
      }}
    >
      {status}
    </span>
  );
}

function IncomingQueue({
  sourceFilter = 'all',
  statusFilter = 'all',
  title = 'Incoming records',
  onSelectRecord,
  selectedRecordId,
  reloadToken = 0,
}) {
  const [search, setSearch] = useState('');
  const [selected, setSelected] = useState(null);
  const { records, loading, error, totalCount, page, pageSize, setPage, reload } = useIncomingRecords({
    source: sourceFilter,
    status: statusFilter,
    search,
  });

  useEffect(() => {
    reload();
  }, [reloadToken, reload]);

  useEffect(() => {
    if (selectedRecordId && records.length) {
      const found = records.find((r) => r.id === selectedRecordId);
      if (found) setSelected(found);
    }
  }, [selectedRecordId, records]);

  const columns = [
    {
      key: 'source',
      label: 'Source',
      render: (row) => row.source.toUpperCase(),
    },
    {
      key: 'type',
      label: 'Type',
      render: (row) => (
        <span style={{ fontSize: '0.85rem' }} title={row.type}>
          {row.type.length > 32 ? `${row.type.slice(0, 32)}…` : row.type}
        </span>
      ),
    },
    {
      key: 'email_hint',
      label: 'Email',
      render: (row) => row.email_hint || '—',
    },
    {
      key: 'status',
      label: 'Status',
      render: (row) => <StatusPill status={row.status} />,
    },
    {
      key: 'occurred_at',
      label: 'When',
      render: (row) =>
        row.occurred_at ? new Date(row.occurred_at).toLocaleString() : '—',
    },
  ];

  const totalPages = Math.max(1, Math.ceil(totalCount / pageSize));

  return (
    <div
      style={{
        display: 'grid',
        gridTemplateColumns: selected ? 'minmax(0, 1.4fr) minmax(0, 1fr)' : '1fr',
        gap: spacing.lg,
        alignItems: 'start',
      }}
    >
      <Card title={title}>
        <div style={{ marginBottom: spacing.md }}>
          <SearchBar
            value={search}
            onChange={setSearch}
            placeholder="Search email, name, external id…"
          />
        </div>
        {loading ? (
          <SkeletonLoader variant="table" count={5} />
        ) : error ? (
          <p style={{ color: colors.error }}>{error}</p>
        ) : records.length === 0 ? (
          <EmptyState
            icon="inbox"
            title={
              sourceFilter === 'stripe'
                ? 'No unmatched Stripe events'
                : sourceFilter === 'nwra'
                  ? 'No NWRA records yet'
                  : 'No incoming records'
            }
            description="New webhooks and imports will appear here."
          />
        ) : (
          <>
            <Table
              columns={columns}
              data={records}
              keyField="id"
              onRowClick={(row) => {
                setSelected(row);
                onSelectRecord?.(row);
              }}
              responsive
            />
            <div
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                marginTop: spacing.md,
                flexWrap: 'wrap',
                gap: spacing.sm,
              }}
            >
              <span style={{ fontSize: '0.85rem', color: colors.midGray }}>
                {totalCount} record(s)
              </span>
              <div style={{ display: 'flex', gap: spacing.sm }}>
                <Button
                  variant="outline"
                  size="sm"
                  disabled={page === 0}
                  onClick={() => setPage((p) => Math.max(0, p - 1))}
                >
                  Previous
                </Button>
                <span style={{ fontSize: '0.85rem', alignSelf: 'center' }}>
                  Page {page + 1} / {totalPages}
                </span>
                <Button
                  variant="outline"
                  size="sm"
                  disabled={page + 1 >= totalPages}
                  onClick={() => setPage((p) => p + 1)}
                >
                  Next
                </Button>
              </div>
            </div>
          </>
        )}
      </Card>
      {selected && (
        <IncomingDetailPanel
          record={selected}
          onClose={() => setSelected(null)}
          onUpdated={() => {
            reload();
            setSelected(null);
          }}
        />
      )}
    </div>
  );
}

export default IncomingQueue;
